import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';

import { fetchEventSource } from '@microsoft/fetch-event-source';
import { Store } from '@ngrx/store';

import { getAccessToken } from '@schaeffler/azure-auth';

import {
  trackBomExportStatusCompleted,
  trackBomExportStatusFailure,
  updateBomExportStatus,
} from '@cdba/core/store/actions/user-interaction/user-interaction.actions';
import { BomExportFeature } from '@cdba/core/store/reducers/user-interaction/user-interaction.reducer';
import { getBomExportFeature } from '@cdba/core/store/selectors/user-interaction/user-interaction.selector';
import { API, BomExportStatusLivePath } from '@cdba/shared/constants/api';

import { UserInteractionDialogComponent } from './dialog/user-interaction-dialog.component';
import { BomExportProgress, BomExportStatus } from './model/feature/bom-export';

@Component({
  selector: 'cdba-user-interaction',
  templateUrl: './user-interaction.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserInteractionComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  private token: string;
  username: string;

  ngOnInit(): void {
    this.store
      .select(getAccessToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((authToken) => {
        this.token = authToken;
      });

    this.store
      .select(getBomExportFeature)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((feature: BomExportFeature) => {
        if (feature.status?.progress === BomExportProgress.STARTED) {
          this.trackBomExportStatus();
        }
      });
  }

  openDialog(): void {
    this.dialog.open(UserInteractionDialogComponent);
  }

  trackBomExportStatus(): void {
    let progress: BomExportProgress;

    fetchEventSource(`${API.v1}/${BomExportStatusLivePath}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      keepalive: true,
      cache: 'no-cache',
      onmessage: (message) => {
        const currentStatus = JSON.parse(message.data);
        progress = (currentStatus as BomExportStatus).progress;
        this.store.dispatch(updateBomExportStatus({ currentStatus }));
      },
      onclose: () => {
        if (
          progress === BomExportProgress.FINISHED ||
          progress === BomExportProgress.FAILED
        ) {
          this.store.dispatch(trackBomExportStatusCompleted());
        } else {
          this.store.dispatch(
            trackBomExportStatusFailure({
              errorMessage: `Wrong progress when closing listener: ${progress}`,
            })
          );
        }
      },
      onerror: (error) => {
        this.store.dispatch(
          trackBomExportStatusFailure({ errorMessage: error.message })
        );
        throw new Error(
          `Error during bom export status tracking: ${error.message}`
        );
      },
    });
  }
}
